import { httpClient } from "../../../shared/api/httpClient";
import type { PaginatedResponse } from "../../../shared/api/pagination";
import type {
  Propose,
  ProposeCompany,
  ProposePayload,
  ProposeRecipient,
} from "../model/types";

export type ProposeListSort = "expires_at_asc" | "expires_at_desc";
export type ProposeSalesBucket = "active" | "history";
export type FetchProposesOptions = {
  representativeId?: string;
  page?: number;
  pageSize?: number;
  status?: Propose["status"];
  search?: string;
  sort?: ProposeListSort;
  salesBucket?: ProposeSalesBucket;
};

export type EligibleProposeProduct = {
  product_code: string;
  product_name?: string | null;
};

export const fetchProposeCompanies = async (
  page = 1,
  pageSize = 20,
  search?: string,
) =>
  (
    await httpClient.get<PaginatedResponse<ProposeCompany>>(
      "/proposes/companies",
      { params: { page, page_size: pageSize, search: search || undefined } },
    )
  ).data;
export const fetchProposes = async ({
  representativeId,
  page = 1,
  pageSize = 20,
  status,
  search,
  sort,
  salesBucket,
}: FetchProposesOptions = {}) =>
  (
    await httpClient.get<PaginatedResponse<Propose>>("/proposes", {
      params: {
        representative_id: representativeId,
        page,
        page_size: pageSize,
        status,
        search: search?.trim() || undefined,
        sort,
        sales_bucket: salesBucket,
      },
    })
  ).data;
export const fetchPropose = async (id: number) =>
  (await httpClient.get<Propose>(`/proposes/${id}`)).data;
export const fetchProposeRecipients = async () =>
  (await httpClient.get<ProposeRecipient[]>("/proposes/recipients")).data;
export const fetchEligibleProposeProducts = async (search?: string) =>
  (
    await httpClient.get<EligibleProposeProduct[]>("/proposes/eligible-products", {
      params: { search: search?.trim() || undefined },
    })
  ).data;
export const createPropose = async (payload: ProposePayload) =>
  (await httpClient.post<Propose>("/proposes", payload)).data;
export const updatePropose = async (id: number, payload: ProposePayload) =>
  (await httpClient.put<Propose>(`/proposes/${id}`, payload)).data;
export const publishPropose = async (id: number) =>
  (await httpClient.post<Propose>(`/proposes/${id}/publish`)).data;
export const confirmPropose = async (id: number) =>
  (await httpClient.post<Propose>(`/proposes/${id}/confirm`)).data;
export const rejectPropose = async (id: number) =>
  (await httpClient.post<Propose>(`/proposes/${id}/reject`)).data;
export const sendPropose = async (id: number) =>
  (await httpClient.post<Propose>(`/proposes/${id}/send`)).data;
const toSubmissionItems = (items: Propose["items"]) =>
  items.map(({ propose_item_id, ...item }) =>
    propose_item_id && propose_item_id > 0
      ? { ...item, propose_item_id }
      : item,
  );
export const submitProposeSalesInput = async (id: number, items: Propose["items"]) =>
  (await httpClient.post<Propose>(`/proposes/${id}/submit`, { items: toSubmissionItems(items) })).data;
export const deletePropose = async (id: number) => {
  await httpClient.delete(`/proposes/${id}`);
};
